/**
 * useFootballFixtures - Centralized hook for football fixtures data fetching,
 * client-side filtering, and sorting.
 *
 * Single source of truth for the fixtures data pipeline shared by both
 * mobile and desktop dashboards, guaranteeing identical results for
 * identical filter inputs.
 *
 * Responsibilities:
 *  1. Fetch fixtures via useRTDBFixtures (real-time cache + API).
 *  2. Apply a client-side league safety-net filter (guards against cache
 *     race conditions that may return broader data than requested).
 *  3. Sort and market-filter via sortFootballFixtures.
 *
 * This hook does NOT own filter state -- callers provide normalized filter
 * values and receive the processed fixture list.
 */

import { useMemo } from "react";
import { useRTDBFixtures } from "../rtdb";
import { sortFootballFixtures } from "../../../../utils/footballLeagueSorting";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { resolveSeasonForLeague } from "../../../../utils/footballSeasonResolver";

/**
 * @param {Object} options
 * @param {string}  options.date     - ISO date string (YYYY-MM-DD)
 * @param {string}  options.league   - League ID string, or '' for all leagues
 * @param {string}  [options.market] - 'all' | 'live' | 'prematch'
 * @param {string}  [options.sort]   - 'smart' | 'time' | 'league'
 * @param {boolean} [options.enabled] - Whether the query should run
 * @returns {{ fixtures: Array, loading: boolean, error: string|null, refetch: Function }}
 */
const useFootballFixtures = ({
  date,
  league,
  market = "all",
  sort = "smart",
  enabled = true,
}) => {
  const isBrazilian = getBrazilianStatus();

  // Resolve the correct API season per-league.
  // Returns null when no league is selected (lets the API return all seasons).
  // Returns the correct season string for a specific league (e.g. "2025" for EPL).
  const season = useMemo(() => resolveSeasonForLeague(league), [league]);

  const fixturesQuery = useRTDBFixtures({
    date,
    league,
    country: "",       // Country filtering is not a user-facing feature
    season,
    enabled,
    realtime: true,
  });

  const filteredAndSortedFixtures = useMemo(() => {
    let results = fixturesQuery.data || [];

    // Client-side league filter as a safety net.
    // The API/cache should return league-specific data when a league is
    // provided, but real-time subscriptions and cache transitions can
    // momentarily deliver broader datasets. This guard ensures only the
    // requested league's fixtures reach the UI.
    if (league) {
      results = results.filter(
        (fixture) => String(fixture?.league?.id) === String(league)
      );
    }

    // Apply sorting and market filtering via the shared utility.
    return sortFootballFixtures(results, {
      isBrazilian,
      sortBy: sort,
      marketFilter: market,
      statusFirst: true,
    });
  }, [fixturesQuery.data, league, market, sort, isBrazilian]);

  return {
    fixtures: filteredAndSortedFixtures,
    rawFixtures: fixturesQuery.data || [],
    loading: fixturesQuery.loading,
    error: fixturesQuery.error,
    refetch: fixturesQuery.refetch,
  };
};

export default useFootballFixtures;
